/**
 * Compose engine inputs from a public bundle and derive standings/brackets in
 * the browser (spec §1: the engine is shared by web and api). One bundle feeds
 * every public page.
 */
import {
  computeStandings,
  computeSeeds,
  detectConflicts,
  resolveBracket,
  type ConflictWarning,
  type EngineTeam,
  type EngineGame,
  type PoolStandings,
  type ResolvedBracket,
  type ResolvedBracketGame,
  type ResolvedSlot,
} from "@alpha/engine";
import { composeFieldKey, teamAttrs } from "@alpha/shared";
import { useQuery } from "@tanstack/react-query";
import { api, ApiError, TOKEN_KEY } from "./api";
import type { Bundle, Game } from "./types";

export function toEngineGame(g: Game): EngineGame {
  return {
    id: g.id,
    type: g.type,
    poolId: g.poolId,
    homeTeamId: g.homeTeamId,
    awayTeamId: g.awayTeamId,
    status: g.status,
    homeScore: g.homeScore,
    awayScore: g.awayScore,
    forfeitTeamId: g.forfeitTeamId,
    scheduledAt: g.scheduledAt,
    fieldKey: composeFieldKey(g.venueId, g.field),
    bracket: g.bracket as EngineGame["bracket"],
  };
}

export function divisionStandings(
  bundle: Bundle,
  divisionId: string,
): { poolId: string; poolName: string; standings: PoolStandings }[] {
  const teamsById = new Map(bundle.teams.map((t) => [t.id, t]));
  const pools = bundle.pools
    .filter((p) => p.divisionId === divisionId)
    .sort((a, b) => a.order - b.order);

  const engineTeams: EngineTeam[] = bundle.entries
    .filter((e) => e.divisionId === divisionId)
    .map((e) => ({
      id: e.teamId,
      name: teamsById.get(e.teamId)?.name ?? e.teamId,
      poolId: e.poolId,
    }));

  const engineGames = bundle.games.filter((g) => g.divisionId === divisionId).map(toEngineGame);
  const all = computeStandings(engineTeams, engineGames, bundle.tournament.config);
  const byPool = new Map(all.map((p) => [p.poolId, p]));

  return pools.map((p) => ({
    poolId: p.id,
    poolName: p.name,
    standings: byPool.get(p.id) ?? { poolId: p.id, rows: [] },
  }));
}

/** Resolve a division's bracket for display (locked seeds if finalized, §7.11). */
export function divisionBracket(bundle: Bundle, divisionId: string): ResolvedBracket {
  const division = bundle.divisions.find((d) => d.id === divisionId);
  const games = bundle.games
    .filter((g) => g.divisionId === divisionId && g.type === "bracket")
    .map(toEngineGame);
  return resolveBracket(games, division?.lockedSeeds ?? null);
}

/** Provisional seeds (pre-lock) for the seeds view. */
export function divisionSeeds(bundle: Bundle, divisionId: string) {
  const standings = divisionStandings(bundle, divisionId).map((s) => s.standings);
  const games = bundle.games.filter((g) => g.divisionId === divisionId).map(toEngineGame);
  return computeSeeds(standings, games, bundle.tournament.config);
}

/**
 * Schedule conflicts (spec §7.13) keyed by game id, for the admin schedule
 * badges. Tournament-wide, not per division: venues and fields are shared across
 * divisions, so a per-division pass would miss real double-bookings.
 */
export function conflictsByGame(bundle: Bundle): Map<string, ConflictWarning[]> {
  const byGame = new Map<string, ConflictWarning[]>();
  for (const w of detectConflicts(bundle.games.map(toEngineGame), bundle.tournament.config)) {
    for (const id of w.gameIds) {
      const list = byGame.get(id);
      if (list) list.push(w);
      else byGame.set(id, [w]);
    }
  }
  return byGame;
}

export function teamName(bundle: Bundle, teamId: string | null | undefined): string {
  if (!teamId) return "TBD";
  return bundle.teams.find((t) => t.id === teamId)?.name ?? teamId;
}

/**
 * A bracket slot as a viewer reads it: the team once the slot resolves, and the
 * engine's own wording for it until then — "Seed 3" before seeds are locked,
 * "Winner Gm 4" while the game feeding it is unplayed.
 *
 * Not `slot.label` on its own. The engine has no team names, so a resolved
 * slot's label is the raw teamId; printing it would put a ULID on the page.
 */
export function slotLabel(bundle: Bundle, slot: ResolvedSlot): string {
  return slot.teamId ? teamName(bundle, slot.teamId) : slot.label;
}

/**
 * A division's resolved bracket games keyed by game id, for a surface that
 * lists games flat rather than by round.
 *
 * The schedule is the reason this exists. A bracket game is stored with no
 * home/away team — who plays it is derived from the locked seeds and from the
 * winners of the games feeding it — so a page that reads `homeTeamId` alone
 * shows "TBD" for a matchup the bracket page names. Resolve once per render and
 * look each game up, rather than per card: every call re-filters the bundle's
 * games and re-walks the bracket, on every render. That is no longer only the
 * poll — the public pages also re-render on a clock tick, to decide which game
 * is being played right now.
 */
export function bracketGamesById(
  bundle: Bundle,
  divisionId: string,
): Map<string, ResolvedBracketGame> {
  return new Map(divisionBracket(bundle, divisionId).games.map((g) => [g.gameId, g]));
}

/**
 * The attributes beside a team's name, for the JSX sites that can render a
 * badge. A sibling to `teamName` rather than a change to it: `teamName`
 * returns a plain string because several of its callers are `<option>` text.
 *
 * Null when the team is unknown or carries no attributes, so a caller renders
 * nothing rather than an empty badge.
 */
export function teamAttrsById(bundle: Bundle, teamId: string | null | undefined): string | null {
  if (!teamId) return null;
  const team = bundle.teams.find((t) => t.id === teamId);
  if (!team) return null;
  return teamAttrs(team) || null;
}

/**
 * The full one-line label for the `<option>` sites, which can hold only text.
 * Falls back to `teamName`'s answer when the team is not in the bundle, so a
 * dropdown never renders an empty entry.
 */
export function teamLabelById(bundle: Bundle, teamId: string | null | undefined): string {
  const name = teamName(bundle, teamId);
  const attrs = teamAttrsById(bundle, teamId);
  return attrs ? `${name} · ${attrs}` : name;
}

export interface TeamOption {
  teamId: string;
  name: string;
}

/**
 * The teams a pool game's home/away slot may be set to: everyone entered in
 * that pool, by name.
 *
 * A team already on the game is included even when it is no longer entered in
 * the pool — an admin can move an entry after the game was built, and a select
 * whose value matches no option silently renders as its first one, which would
 * show the wrong team and write it on the next save. The API only enforces
 * membership on assignment, so leaving a stale pairing alone is legal; it just
 * has to be visible.
 */
export function poolTeamOptions(
  bundle: Bundle,
  game: Pick<Game, "poolId" | "homeTeamId" | "awayTeamId">,
): TeamOption[] {
  const teamIds = bundle.entries.filter((e) => e.poolId === game.poolId).map((e) => e.teamId);
  const options = [...new Set(teamIds)]
    .map((teamId) => ({ teamId, name: teamName(bundle, teamId) }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const known = new Set(options.map((o) => o.teamId));
  for (const teamId of [game.homeTeamId, game.awayTeamId]) {
    if (teamId && !known.has(teamId)) {
      known.add(teamId);
      options.push({ teamId, name: teamName(bundle, teamId) });
    }
  }
  return options;
}

export type BundleViewState = "loading" | "notFound" | "error" | "ready";

/**
 * Which view a bundle page should render.
 *
 * Only a 404 means "not found", and it is checked FIRST — ahead of retained
 * data — because react-query keeps the last successful `data` when a refetch
 * fails. Without that ordering, a tournament archived while a coach had the
 * page open would keep rendering from the stale bundle forever.
 *
 * Every OTHER failure deliberately loses to retained data. This app is used at
 * outdoor ballfields on bad cell service, so a dropped connection is routine,
 * not exceptional — and react-query only sets `isError` after exhausting its
 * retries, meaning a sustained outage. Showing a slightly stale schedule beats
 * telling a coach their tournament does not exist because their signal died.
 * With no data to fall back on, `"error"` says we could not load it rather
 * than claiming it is missing.
 */
export function bundleViewState(q: {
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  data: Bundle | undefined;
}): BundleViewState {
  if (q.error instanceof ApiError && q.error.status === 404) return "notFound";
  if (q.data) return "ready";
  if (q.isError) return "error";
  return "loading";
}

/**
 * Fetch a public bundle with a 30s polling fallback for live updates (§10).
 *
 * Archived tournaments 404 on the public tier by design. A signed-in coach
 * whose team played in one may still read it, so a 404 retries against the
 * authenticated route. Live tournaments never reach that second request, so
 * the common path stays on the unauthenticated, cacheable endpoint.
 *
 * If that retry fails, the ORIGINAL 404 is what surfaces. `TOKEN_KEY` only
 * tells us a token exists, not that it is still valid — an expired one makes
 * the authenticated route 401, and reporting that would show "couldn't load"
 * to someone who should simply be told the tournament isn't there, which is
 * exactly what a signed-out visitor sees in the same position.
 */
export function useBundle(tid: string | undefined) {
  return useQuery({
    queryKey: ["bundle", tid],
    enabled: !!tid,
    // Archived tournaments are frozen, so polling them buys nothing and costs
    // two requests a tick — the public leg is guaranteed to 404 before the
    // authed one succeeds. Stopping also removes a real failure mode: nothing
    // here refreshes an expiring token, so a long-open archived page would
    // eventually see its authed retry 401, surface the original 404, and
    // replace a perfectly good page with "Tournament not found".
    refetchInterval: (q) => (q.state.data?.tournament.status === "archived" ? false : 30_000),
    queryFn: async () => {
      try {
        return await api.get<Bundle>(`/public/tournaments/${tid}/bundle`);
      } catch (e) {
        const signedIn = Boolean(localStorage.getItem(TOKEN_KEY));
        if (e instanceof ApiError && e.status === 404 && signedIn) {
          try {
            return await api.get<Bundle>(`/me/tournaments/${tid}/bundle`);
          } catch {
            throw e;
          }
        }
        throw e;
      }
    },
  });
}
